import { DiscountCode } from '@/util/types/serverTypeCollection/discounts';

export type DiscountCodeValidationResult = {
  isValid: boolean;
  error?: string;
  discountCode?: DiscountCode;
};

/**
 * Whether a code is active and inside its date window right now, ignoring
 * service eligibility.
 *
 * Use this to *filter* lists of codes (e.g. the codes an admin may attach to a
 * campaign link). Use `validateDiscountCode` when a customer submits a specific
 * code and needs to be told which check failed.
 */
export function isDiscountCodeRedeemable(
  discountCode: Pick<DiscountCode, 'active' | 'startsAt' | 'expiresAt'>,
  now: Date = new Date()
): boolean {
  if (!discountCode.active) return false;
  if (discountCode.startsAt && new Date(discountCode.startsAt) > now) return false;
  if (discountCode.expiresAt && new Date(discountCode.expiresAt) <= now) return false;
  return true;
}

/**
 * Validates a discount code for eligibility
 *
 * @param code - The discount code string entered by user
 * @param discountCodes - Array of all available discount codes
 * @param selectedServiceIds - Currently selected service IDs
 * @returns Validation result with error message if invalid, or discount code if valid
 */
export function validateDiscountCode(
  code: string,
  discountCodes: DiscountCode[],
  selectedServiceIds: string[]
): DiscountCodeValidationResult {
  // Normalize code (uppercase, trim)
  const normalizedCode = code.toUpperCase().trim();

  if (!normalizedCode) {
    return {
      isValid: false,
      error: 'Please enter a discount code',
    };
  }

  // Find matching discount code
  const discountCode = discountCodes.find(
    (dc) => dc.code.toUpperCase() === normalizedCode
  );

  if (!discountCode) {
    return {
      isValid: false,
      error: 'Invalid discount code',
    };
  }

  // Check if code is active
  if (!discountCode.active) {
    return {
      isValid: false,
      error: 'This discount code is not active',
    };
  }

  // Check expiration dates
  const now = new Date();

  if (discountCode.startsAt) {
    const startsAt = new Date(discountCode.startsAt);
    if (now < startsAt) {
      return {
        isValid: false,
        error: `This discount code is not valid until ${startsAt.toLocaleDateString()}`,
      };
    }
  }

  if (discountCode.expiresAt) {
    const expiresAt = new Date(discountCode.expiresAt);
    if (now > expiresAt) {
      return {
        isValid: false,
        error: `This discount code expired on ${expiresAt.toLocaleDateString()}`,
      };
    }
  }

  // Check service eligibility
  if (!discountCode.appliesToAllServices) {
    const eligibleServiceIds = discountCode._serviceIds || [];
    
    if (eligibleServiceIds.length === 0) {
      return {
        isValid: false,
        error: 'This discount code is not applicable to any services',
      };
    }

    // Check if at least one selected service is eligible
    const hasEligibleService = selectedServiceIds.some((id) =>
      eligibleServiceIds.includes(id)
    );

    if (!hasEligibleService) {
      return {
        isValid: false,
        error: 'This discount code is not applicable to your selected services',
      };
    }
  }

  // All checks passed
  return {
    isValid: true,
    discountCode,
  };
}

/**
 * The part of a discount code that decides *which* services it covers.
 *
 * Taken as a `Pick` so callers holding a partial code — a draft in the settings
 * form, a code narrowed for a test — can ask the coverage question without
 * assembling a whole `DiscountCode`.
 */
export type DiscountCoverage = Pick<
  DiscountCode,
  'appliesToAllServices' | '_serviceIds'
>;

/**
 * Whether this code would discount that one service.
 *
 * Coverage only — it says nothing about whether the code is active, in date, or
 * currently in the cart. Use `validateDiscountCode` when a customer submits a
 * code and needs to be told which check failed.
 */
export function discountCoversService(
  discountCode: DiscountCoverage,
  serviceId: string
): boolean {
  if (discountCode.appliesToAllServices) return true;
  return (discountCode._serviceIds || []).includes(serviceId);
}

/**
 * The subset of `candidateServiceIds` this code would discount, in the order
 * they were given.
 *
 * The candidates do not have to be in the cart. Pass the cart to ask "what is
 * this code discounting right now"; pass the catalog to ask "what could the
 * customer add to make this code work" — which is how the scheduler turns a
 * code that does not apply yet into a next step instead of a dead end.
 */
export function getDiscountCoveredServiceIds(
  discountCode: DiscountCoverage,
  candidateServiceIds: string[]
): string[] {
  return candidateServiceIds.filter((id) =>
    discountCoversService(discountCode, id)
  );
}

/**
 * Gets eligible service IDs for a discount code
 * 
 * @param discountCode - The discount code
 * @param selectedServiceIds - Currently selected service IDs
 * @returns Array of service IDs that are eligible for this discount
 */
export function getEligibleServiceIds(
  discountCode: DiscountCode,
  selectedServiceIds: string[]
): string[] {
  return getDiscountCoveredServiceIds(discountCode, selectedServiceIds);
}



